// @ts-nocheck
/**
 * DXF関連エクスポート/インポート関数
 * downLoad.jsから分離
 */
import store from '@/store'
import proj4 from 'proj4'
import JSZip from 'jszip'
import * as turf from '@turf/turf'
import axios from 'axios'
import {
  zahyokei,
  exportLayerToGeoJSON,
  extractHighlightedGeoJSONFromSource,
  getChibanAndHoka,
  getKeiByCode,
  wsg84ToJgdForGeojson,
  getSourceData,
  getNowFileNameTimestamp,
} from './downLoad'

// DXFハイライト用フラグ（downLoad.jsのisFirstRunと同等）
let isFirstRun = true

/**
 * GeoJSONをDXF形式に変換
 * Polygon、MultiPolygon、内部リング（穴）に完全対応
 * 地番テキストのラベル出力にも対応
 * Jw_cad、TREND-ONE（トレンドワン）互換
 * @param {Object} geojson - GeoJSONオブジェクト
 * @param {string} cadType - CADタイプ ('2d' または 'survey')
 */
export function geojsonToDXF(geojson, cadType = '2d') {
  // 測量・3DCAD用は昔のシンプルな形式を使用（福井/アイサン/AutoCAD互換）
  let dxf
  if (cadType === 'survey') {
    // 測量・3DCAD用：シンプルなヘッダー（昔の形式）
    dxf =
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'HEADER\n' +
      '9\n$INSUNITS\n70\n6\n' + // 単位をメートルに設定
      '0\n' +
      'ENDSEC\n' +
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'TABLES\n' +
      '0\n' +
      'ENDSEC\n' +
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'ENTITIES\n'
  } else {
    // 2D CAD用：拡張ヘッダー（TREND-ONE互換）
    dxf =
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'HEADER\n' +
      '9\n$ACADVER\n1\nAC1015\n' + // AutoCAD 2000形式（TREND-ONE互換性向上）
      '9\n$INSUNITS\n70\n6\n' + // 単位をメートルに設定
      '9\n$MEASUREMENT\n70\n1\n' + // メートル法を明示
      '0\n' +
      'ENDSEC\n' +
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'TABLES\n' +
      '0\n' +
      'TABLE\n' + // レイヤーテーブル開始
      '2\nLAYER\n' +
      '70\n1\n' + // 最大エントリ数
      '0\n' +
      'LAYER\n' + // デフォルトレイヤー定義
      '2\n0\n' + // レイヤー名"0"
      '70\n0\n' + // フラグ
      '62\n7\n' + // 色番号（7=白）
      '6\nCONTINUOUS\n' + // 線種名
      '0\n' +
      'ENDTAB\n' + // レイヤーテーブル終了
      '0\n' +
      'ENDSEC\n' +
      '0\n' +
      'SECTION\n' +
      '2\n' +
      'ENTITIES\n'
  }

  function processPoint(coord, layer = 'Default') {
    if (cadType === 'survey') {
      // 測量・3DCAD用：Z座標なし（昔の形式）
      return '0\n' + 'POINT\n' + '8\n' + layer + '\n' + '10\n' + coord[0] + '\n' + '20\n' + coord[1] + '\n'
    } else {
      // 2D CAD用：Z座標あり
      return (
        '0\n' +
        'POINT\n' +
        '8\n' +
        layer +
        '\n' +
        '10\n' +
        coord[0] +
        '\n' +
        '20\n' +
        coord[1] +
        '\n' +
        '30\n' +
        (coord[2] || 0.0) +
        '\n'
      )
    }
  }

  function processLineString(coords, layer = 'Default') {
    let dxfPart = '0\n' + 'LWPOLYLINE\n' + '8\n' + layer + '\n' + '90\n' + coords.length + '\n'
    coords.forEach((coord) => {
      dxfPart += '10\n' + coord[0] + '\n' + '20\n' + coord[1] + '\n'
    })
    return dxfPart
  }

  /**
   * Polygon処理: 外部リングと内部リング（穴）の両方に対応
   * coords[0]: 外部リング
   * coords[1], coords[2]...: 内部リング（穴）
   */
  function processPolygon(coords, layer = 'Default') {
    if (cadType === 'survey') {
      // 測量・3DCAD用：シンプルな形式（外部リングのみ、昔の形式）
      let dxfPart = '0\n' + 'LWPOLYLINE\n' + '8\n' + layer + '\n' + '90\n' + coords[0].length + '\n' + '70\n1\n'
      coords[0].forEach((coord) => {
        dxfPart += '10\n' + coord[0] + '\n' + '20\n' + coord[1] + '\n'
      })
      return dxfPart
    } else {
      // 2D CAD用：各辺をLINEエンティティとして出力（Jw_cadで分解不要）
      let dxfPart = ''

      // --- 元のLWPOLYLINE版（戻す場合はここのコメントを外してLINE版をコメントアウト） ---
      // coords.forEach((ring, index) => {
      //   if (!ring || ring.length < 3) {
      //     console.warn(`Invalid polygon ring at index ${index}`)
      //     return
      //   }
      //   dxfPart += '0\n' + 'LWPOLYLINE\n' + '8\n' + layer + '\n' + '90\n' + ring.length + '\n' + '70\n1\n'
      //   ring.forEach((coord) => {
      //     dxfPart += '10\n' + coord[0] + '\n' + '20\n' + coord[1] + '\n'
      //   })
      // })
      // --- 元のLWPOLYLINE版ここまで ---

      // --- LINE版（Jw_cadで各辺を個別に伸縮可能） ---
      coords.forEach((ring, index) => {
        if (!ring || ring.length < 3) {
          console.warn(`Invalid polygon ring at index ${index}`)
          return
        }

        for (let i = 0; i < ring.length - 1; i++) {
          const from = ring[i]
          const to = ring[i + 1]
          dxfPart +=
            '0\nLINE\n' +
            '8\n' + layer + '\n' +
            '10\n' + from[0] + '\n' +
            '20\n' + from[1] + '\n' +
            '30\n' + (from[2] || 0.0) + '\n' +
            '11\n' + to[0] + '\n' +
            '21\n' + to[1] + '\n' +
            '31\n' + (to[2] || 0.0) + '\n'
        }
        // 閉合: 最後の点→最初の点（リングが閉じていない場合）
        const first = ring[0]
        const last = ring[ring.length - 1]
        if (first[0] !== last[0] || first[1] !== last[1]) {
          dxfPart +=
            '0\nLINE\n' +
            '8\n' + layer + '\n' +
            '10\n' + last[0] + '\n' +
            '20\n' + last[1] + '\n' +
            '30\n' + (last[2] || 0.0) + '\n' +
            '11\n' + first[0] + '\n' +
            '21\n' + first[1] + '\n' +
            '31\n' + (first[2] || 0.0) + '\n'
        }
      })
      // --- LINE版ここまで ---

      return dxfPart
    }
  }

  /**
   * テキスト出力（地番ラベル用）
   * CADタイプによって出力形式を分岐
   * @param {string} text - 表示するテキスト
   * @param {Array} coord - [x, y, z] 座標
   * @param {number} height - テキスト高さ
   * @param {string} layer - レイヤー名
   */
  function processText(text, coord, height = 1.0, layer = 'Default') {
    if (!text || text === '') return ''

    if (cadType === 'survey') {
      // 測量・3D CAD用（福井/アイサン/AutoCAD）
      // 最もシンプルで標準的な書き方（Z座標なし、配置方法なし）
      return (
        '0\n' +
        'TEXT\n' +
        '8\n' +
        layer +
        '\n' +
        '10\n' +
        coord[0] +
        '\n' + // 基準点X座標
        '20\n' +
        coord[1] +
        '\n' + // 基準点Y座標
        '40\n' +
        height +
        '\n' + // テキスト高さ
        '1\n' +
        text +
        '\n' + // テキスト文字列
        '72\n1\n' + // 水平方向：中央揃え
        '73\n2\n'
      ) // 垂直方向：中央揃え
    } else {
      // 2D CAD用（jww/HO-CAD）
      // Jw_cad、TREND-ONE互換性を考慮した実装
      const z = coord[2] || 0.0 // Z座標（測量CAD対応）
      return (
        '0\n' +
        'TEXT\n' +
        '8\n' +
        layer +
        '\n' +
        '10\n' +
        coord[0] +
        '\n' + // 基準点X座標
        '20\n' +
        coord[1] +
        '\n' + // 基準点Y座標
        '30\n' +
        z +
        '\n' + // 基準点Z座標（測量CAD対応）
        '40\n' +
        height +
        '\n' + // テキスト高さ
        '1\n' +
        text +
        '\n' + // テキスト文字列
        '72\n1\n' + // 水平方向：中央揃え（0=左, 1=中央, 2=右）
        '73\n2\n' + // 垂直方向：中央揃え（0=ベースライン, 1=下, 2=中央, 3=上）
        '11\n' +
        coord[0] +
        '\n' + // 配置点X座標
        '21\n' +
        coord[1] +
        '\n' + // 配置点Y座標
        '31\n' +
        z +
        '\n'
      ) // 配置点Z座標（測量CAD対応）
    }
  }

  /**
   * ポリゴンの重心を計算（簡易版：外部リングの座標平均）
   */
  function calculateCentroid(coords) {
    const exteriorRing = coords[0]
    if (!exteriorRing || exteriorRing.length === 0) return null

    let sumX = 0,
      sumY = 0
    exteriorRing.forEach((coord) => {
      sumX += coord[0]
      sumY += coord[1]
    })

    return [sumX / exteriorRing.length, sumY / exteriorRing.length]
  }

  function processMultiPoint(coords, layer = 'Default') {
    return coords.map((coord) => processPoint(coord, layer)).join('')
  }

  function processMultiLineString(coords, layer = 'Default') {
    return coords.map((line) => processLineString(line, layer)).join('')
  }

  function processMultiPolygon(coords, layer = 'Default') {
    return coords.map((polygon) => processPolygon(polygon, layer)).join('')
  }

  geojson.features.forEach((feature) => {
    const geometry = feature.geometry
    const properties = feature.properties || {}
    const layer = properties.layer || 'Default'

    try {
      switch (geometry.type) {
        case 'Point':
          dxf += processPoint(geometry.coordinates, layer)
          break
        case 'LineString':
          dxf += processLineString(geometry.coordinates, layer)
          break
        case 'Polygon': {
          dxf += processPolygon(geometry.coordinates, layer)

          // 地番ラベルを追加（2D CADの場合のみ）
          if (cadType === '2d') {
            const chibanLabel = properties['地番'] || properties['chiban'] || properties['label']
            if (chibanLabel) {
              const centroid = calculateCentroid(geometry.coordinates)
              if (centroid) {
                dxf += processText(chibanLabel, centroid, 2.0, layer)
              }
            }
          }
          break
        }
        case 'MultiPoint':
          dxf += processMultiPoint(geometry.coordinates, layer)
          break
        case 'MultiLineString':
          dxf += processMultiLineString(geometry.coordinates, layer)
          break
        case 'MultiPolygon': {
          dxf += processMultiPolygon(geometry.coordinates, layer)

          // MultiPolygonの場合、最初のポリゴンの重心に地番を配置（2D CADの場合のみ）
          if (cadType === '2d') {
            const multiChibanLabel = properties['地番'] || properties['chiban'] || properties['label']
            if (multiChibanLabel && geometry.coordinates.length > 0) {
              const multiCentroid = calculateCentroid(geometry.coordinates[0])
              if (multiCentroid) {
                dxf += processText(multiChibanLabel, multiCentroid, 2.0, layer)
              }
            }
          }
          break
        }
        default:
          console.warn('サポートされていないジオメトリタイプ: ' + geometry.type)
          break
      }
    } catch (error) {
      console.error('Feature処理中にエラー:', error, feature)
    }
  })

  store.commit('setLoading', false)

  dxf += '0\n' + 'ENDSEC\n' + '0\n' + 'EOF\n'

  return dxf
}

export function saveDXfGaiku(map, geojson, zahyo) {
  const layerIds = []
  if (map.getLayer('oh-gaiku-layer')) {
    layerIds.push('oh-gaiku-layer')
  }
  if (map.getLayer('oh-toshikan-layer')) {
    layerIds.push('oh-toshikan-layer')
  }
  const features = map.queryRenderedFeatures({
    layers: layerIds, // 対象のレイヤー名を指定
  })
  if (features.length === 0) {
    return geojson
  }
  // GeoJSON形式に変換
  const gaikuGeojson = {
    type: 'FeatureCollection',
    features: features.map((feature) => ({
      type: 'Feature',
      geometry: feature.geometry,
      properties: feature.properties,
    })),
  }


  gaikuGeojson.features.forEach((feature) => {
    let x, y
    if (feature.properties.補正後X座標) {
      x = feature.properties.補正後X座標
      y = feature.properties.補正後Y座標
    } else {
      x = feature.properties.X座標
      y = feature.properties.Y座標
    }
    feature.geometry.coordinates = [y, x]
  })
  // 新しいGeoJSONオブジェクトを作成
  geojson = {
    type: 'FeatureCollection',
    features: [...geojson.features, ...gaikuGeojson.features], // featuresを結合
  }
  return geojson
}

export async function saveDxf(map, layerId, sourceId, fields, detailGeojson, kei2, cadType = '2d', isFgb = false, chiban = null) {
  let geojson
  if (detailGeojson) {
    geojson = detailGeojson
  } else {
    if (map.getZoom() <= 15) {
      alert('ズーム15以上にしてください。')
      return
    }
    geojson = exportLayerToGeoJSON(map, layerId, sourceId, fields)
  }
  geojson = extractHighlightedGeoJSONFromSource(geojson, layerId)
  let zahyo
  if (kei2) {
    zahyo = kei2
  } else {
    for (const feature of geojson.features) {
      if (feature.properties && feature.properties.座標系) {
        zahyo = feature.properties.座標系 // 最初に見つけた値を返す
      }
    }
  }
  const code = zahyokei.find((item) => item.kei === zahyo).code
  const kei = zahyokei.find((item) => item.kei === zahyo).kei
  function transformGeoJSON(geojson, crsCode) {
    if (!geojson || !geojson.type) {
      console.warn('⚠️ 無効なGeoJSONデータです。')
      return null
    }

    // 再帰的に座標を変換する関数
    function transformCoordinates(coords) {
      if (Array.isArray(coords[0])) {
        return coords.map(transformCoordinates)
      } else if (Array.isArray(coords) && coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        return proj4('EPSG:4326', code, coords)
      } else {
        console.warn('⚠️ 無効な座標が検出され、スキップされました:', coords)
        return null
      }
    }

    // 再帰的にジオメトリを処理する関数
    function transformGeometry(geometry) {
      if (!geometry || !geometry.type) return null

      const transformed = { ...geometry }

      switch (geometry.type) {
        case 'Point':
          transformed.coordinates = transformCoordinates(geometry.coordinates)
          break
        case 'LineString':
        case 'MultiPoint':
          transformed.coordinates = geometry.coordinates.map(transformCoordinates)
          break
        case 'Polygon':
        case 'MultiLineString':
          transformed.coordinates = geometry.coordinates.map((ring) => ring.map(transformCoordinates))
          break
        case 'MultiPolygon':
          transformed.coordinates = geometry.coordinates.map((polygon) =>
            polygon.map((ring) => ring.map(transformCoordinates))
          )
          break
        default:
          console.warn(`⚠️ 未対応のジオメトリタイプ: ${geometry.type}`)
          return null
      }

      return transformed
    }

    // FeatureとFeatureCollectionを処理
    if (geojson.type === 'Feature') {
      return {
        ...geojson,
        geometry: transformGeometry(geojson.geometry),
      }
    } else if (geojson.type === 'FeatureCollection') {
      return {
        ...geojson,
        features: geojson.features.map((feature) => ({
          ...feature,
          geometry: transformGeometry(feature.geometry),
        })),
      }
    } else if (geojson.type === 'GeometryCollection') {
      return {
        ...geojson,
        geometries: geojson.geometries.map(transformGeometry),
      }
    } else {
      return transformGeometry(geojson)
    }
  }

  geojson = transformGeoJSON(geojson, code)

  // 各頂点をポイントとして追加
  function addPolygonVerticesAsPoints(geojson) {
    const pointFeatures = []

    geojson.features.forEach((feature) => {
      if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
        const coordinates =
          feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates

        coordinates.forEach((polygon) => {
          polygon.forEach((ring) => {
            ring.forEach((coord) => {
              pointFeatures.push({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: coord,
                },
                properties: { source: 'vertex', parent: feature.properties.name || null },
              })
            })
          })
        })
      }
    })

    return {
      ...geojson,
      features: [...geojson.features, ...pointFeatures],
    }
  }

  geojson = addPolygonVerticesAsPoints(geojson)

  geojson = saveDXfGaiku(map, geojson, zahyo)

  // convertGeoJSON(geojson,code) // ここを復活するとPHP版になる。
  store.commit('setLoading', false)

  try {
    const dxfString = geojsonToDXF(geojson, cadType)

    // データベースに保存
    const firstChiban = getChibanAndHoka(geojson, true, chiban).firstChiban
    const hoka = getChibanAndHoka(geojson, true, chiban).hoka
    const kei = getKeiByCode(code)
    const fgbSuffix = isFgb ? '_fgb' : ''
    const fileNameWithoutExt = kei + firstChiban + hoka + fgbSuffix
    const fileName = fileNameWithoutExt + '.dxf'

    // マイルーム保存を無効化（シンプルに出力のみ）
    // if (store.state.userId) {
    //   try {
    //     const response = await axios.post(
    //       '/oh3/php/userDxf.php',
    //       new URLSearchParams({
    //         action: 'insert',
    //         uid: store.state.userId,
    //         name: fileNameWithoutExt,
    //         dxftext: dxfString,
    //       })
    //     )
    //     if (response.data.error) {
    //       console.error('データベース保存エラー:', response.data.error)
    //     } else {
    //       store.commit('setFetchImagesFire', !store.state.fetchImagesFire)
    //     }
    //   } catch (dbError) {
    //     console.error('データベース保存中にエラーが発生しました:', dbError)
    //   }
    // }

    // DXFファイルとしてダウンロード
    const blob = new Blob([dxfString], { type: 'application/dxf' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName

    link.click()
  } catch (error) {
    console.error('GeoJSONの解析中にエラーが発生しました:', error)
    alert('有効なGeoJSONを入力してください。')
  }
}

export function highlightSpecificFeaturesDxf(map, layerId) {
  const isPoint = layerId.includes('-point-layer')
  let sec = 0
  if (isFirstRun) {
    sec = 0
  } else {
    sec = 0
  }
  try {
    setTimeout(() => {
      if (layerId.includes('-polygon-layer')) {
        map.setPaintProperty(layerId, 'fill-color', [
          'case',
          ['in', ['concat', ['get', 'handle']], ['literal', Array.from(store.state.highlightedDxfs)]],
          'rgba(0, 150, 255, 0.45)', // OH3スタイリッシュブルー
          'rgba(0, 0, 0, 0)', // クリックされていない場合は透明（SIMAと同じ）
        ])
      } else if (layerId.includes('-line-layer')) {
        map.setPaintProperty(layerId, 'line-color', [
          'case',
          ['in', ['concat', ['get', 'handle']], ['literal', Array.from(store.state.highlightedDxfs)]],
          'rgba(0, 150, 255, 1)', // OH3スタイリッシュブルー
          '#000', // されてない場合（デフォルトの黒）
        ])
        map.setPaintProperty(layerId, 'line-width', [
          'case',
          ['in', ['concat', ['get', 'handle']], ['literal', Array.from(store.state.highlightedDxfs)]],
          3, // 選択時は太く
          2, // デフォルト（レイヤー定義と同じ）
        ])
      } else if (layerId.includes('-point-layer')) {
        map.setPaintProperty(layerId, 'circle-color', [
          'case',
          ['in', ['concat', ['get', 'handle']], ['literal', Array.from(store.state.highlightedDxfs)]],
          'rgba(0, 150, 255, 1)', // OH3スタイリッシュブルー
          'navy', // されてない場合
        ])
        map.setPaintProperty(layerId, 'circle-stroke-width', 3)
      }
    }, sec)
    isFirstRun = false
  } catch (e) {
  }
}

export async function zipDownloadDxfText(dxfTexts) {
  const zip = new JSZip()
  let blob
  dxfTexts.forEach((dxfText) => {
    blob = new Blob([dxfText.dxfText], { type: 'application/octet-stream' })
    zip.file(dxfText.name + '.dxf', blob)
  })

  if (dxfTexts.length > 1) {
    // ZIPファイルを生成してダウンロード
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const zipLink = document.createElement('a')
    zipLink.href = URL.createObjectURL(zipBlob)
    zipLink.download = 'dxfzip.zip'
    zipLink.click()
    URL.revokeObjectURL(zipLink.href)
  } else {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = dxfTexts[0].name + '.dxf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }
}

export function saveDxfForChiriin(map, layerIds) {
  function getGeoJSONFromLayers(map, layerIds) {
    let allFeatures = []
    layerIds.forEach((layerId) => {
      const bounds = map.getBounds() // 表示範囲を取得
      const features = map.queryRenderedFeatures(bounds, {
        layers: [layerId], // 適切なレイヤー名を指定
      })
      if (features.length > 0) {
        allFeatures = allFeatures.concat(features)
      }
    })
    return {
      type: 'FeatureCollection',
      features: allFeatures,
    }
  }
  const geojson = wsg84ToJgdForGeojson(getGeoJSONFromLayers(map, layerIds))
  try {
    const dxfString = geojsonToDXF(geojson)
    // DXFファイルとしてダウンロード
    const blob = new Blob([dxfString], { type: 'application/dxf' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'vector.dxf'
    link.click()
  } catch (error) {
    console.error('GeoJSONの解析中にエラーが発生しました:', error)
    alert('有効なGeoJSONを入力してください。')
  }
}

export function dxfToGeoJSON(dxf, inputZahyokei = null) {
  // 座標変換関数を定義
  const transformCoordinates = (coords) => {
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      // DXFの座標系からWGS84に変換
      // 明示的に渡された座標系を優先、次にdxfText.zahyokei、最後にstore.zahyokei
      const zahyo = inputZahyokei || store.state.dxfText?.zahyokei || store.state.zahyokei
      if (zahyo && zahyo !== 'WGS84') {
        const code = zahyokei.find((item) => item.kei === zahyo)?.code
        if (code) {
          return proj4(code, 'EPSG:4326', coords)
        }
      }
      return coords
    }
    return coords.map(transformCoordinates)
  }

  const features = []
  const vertices = new Set() // 頂点座標を追跡（重複排除用）

  dxf.entities.forEach((entity) => {
    if (entity.type === 'LINE') {
      const start = entity.start || entity.vertices?.[0]
      const end = entity.end || entity.vertices?.[1]
      if (start && end) {
        const startCoord = transformCoordinates([start.x, start.y])
        const endCoord = transformCoordinates([end.x, end.y])

        features.push(turf.lineString([startCoord, endCoord], { ...entity }))

        // 頂点を追跡
        vertices.add(JSON.stringify(startCoord))
        vertices.add(JSON.stringify(endCoord))
      }
    } else if (entity.type === 'POINT') {
      const pos = entity.position || entity
      if (pos.x !== undefined && pos.y !== undefined) {
        features.push(turf.point(transformCoordinates([pos.x, pos.y]), { type: 'point', ...entity }))
      }
    } else if (entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE') {
      if (entity.vertices && entity.vertices.length > 1) {
        const coordinates = entity.vertices.map((vertex) => transformCoordinates([vertex.x, vertex.y]))
        const isClosed =
          entity.closed === true ||
          entity.shape === true ||
          (entity.vertices.length > 2 &&
            entity.vertices[0].x === entity.vertices[entity.vertices.length - 1].x &&
            entity.vertices[0].y === entity.vertices[entity.vertices.length - 1].y)

        if (isClosed && coordinates.length >= 3) {
          const ring = [...coordinates]
          const first = ring[0]
          const last = ring[ring.length - 1]
          if (first[0] !== last[0] || first[1] !== last[1]) {
            ring.push(first)
          }
          features.push(turf.polygon([ring], { ...entity }))
        } else {
          features.push(turf.lineString(coordinates, { ...entity }))
        }

        // 頂点を追跡
        coordinates.forEach((coord) => {
          vertices.add(JSON.stringify(coord))
        })
      }
    } else if (entity.type === 'TEXT' || entity.type === 'MTEXT') {
      const pos = entity.position || entity.startPoint || entity
      if (pos.x !== undefined && pos.y !== undefined) {
        features.push(turf.point(transformCoordinates([pos.x, pos.y]), { ...entity }))
      }
    }
    // 必要ならCIRCLE, ARC, ELLIPSEなども同様に
  })

  // 頂点をポイントフィーチャーとして追加
  vertices.forEach((coordStr) => {
    const coord = JSON.parse(coordStr)
    features.push(turf.point(coord, { type: 'vertex' }))
  })

  return {
    type: 'FeatureCollection',
    features,
  }
}

export function DXFDownload() {
  const map01 = store.state.map01
  const source = map01.getSource('click-circle-source')
  const originalGeojson = getSourceData(source)
  if (!originalGeojson) {
    alert('ソースが見つかりません')
    return
  }
  // endpointプロパティがないフィーチャだけを残す
  const filteredFeatures = originalGeojson.features.filter((feature) => !('endpoint' in feature.properties))
  const filteredGeojson = {
    type: 'FeatureCollection',
    features: filteredFeatures,
  }
  const toEPSG = zahyokei.find((item) => item.kei === store.state.zahyokei).code
  const convertedGeojson = convertFromEPSG4326(filteredGeojson, toEPSG)
  const dxfString = geojsonToDXF(convertedGeojson)
  // Blobとしてダウンロード処理
  const blob = new Blob([dxfString], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = getNowFileNameTimestamp() + '.dxf'
  a.click()
  URL.revokeObjectURL(url) // 後片付け
}

/**
 * GeoJSONの座標を EPSG:4326 から任意の EPSG に変換する
 * @param {object} geojson - GeoJSON FeatureCollection
 * @param {string} toEPSG - 変換先のEPSGコード（例: 'EPSG:2450'）
 * @returns {object} - 変換後のGeoJSON
 */
export function convertFromEPSG4326(geojson, toEPSG) {
  const fromEPSG = 'EPSG:4326'
  // alert(toEPSG)
  const transformCoords = (coords) => {
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return proj4(fromEPSG, toEPSG, coords)
    }
    return coords.map(transformCoords)
  }

  const transformGeometry = (geometry) => {
    if (!geometry) return null

    if (geometry.type === 'GeometryCollection') {
      return {
        ...geometry,
        geometries: geometry.geometries.map(transformGeometry),
      }
    }

    return {
      ...geometry,
      coordinates: transformCoords(geometry.coordinates),
    }
  }

  return {
    ...geojson,
    features: geojson.features.map((feature) => ({
      ...feature,
      geometry: transformGeometry(feature.geometry),
    })),
  }
}

